import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, ExternalLink, Eye } from 'lucide-react';
import { toast } from 'sonner';

// Fix O (2026-09-10) — Alian: "quiero ver la lista de lo que se ha pagado...
// no quiero editar el pago ni nada de eso". READ-ONLY by design: no edit/
// delete/mark-as-paid controls anywhere in this dialog, on purpose.
const STATUS_STYLE = {
  paid: 'bg-emerald-50 text-emerald-700',
  unpaid: 'bg-amber-50 text-amber-700',
};

function methodLabel(e, t) {
  if (!e.payment_method) return '—';
  if (e.payment_method === 'check') {
    const num = e.check_number || e.payment_reference;
    return num ? `${t('expenses.paymentMethods.check')} #${num}` : t('expenses.paymentMethods.check');
  }
  return t(`expenses.paymentMethods.${e.payment_method}`, { defaultValue: e.payment_method });
}

export const BeneficiaryDetailDialog = ({ open, onClose, beneficiaryName }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !beneficiaryName) return;
    setLoading(true);
    setData(null);
    api.get(`/expenses/by-beneficiary/${encodeURIComponent(beneficiaryName)}/detail`)
      .then(res => setData(res.data))
      .catch(() => toast.error(t('errors.loadingError')))
      .finally(() => setLoading(false));
  }, [open, beneficiaryName, t]);

  const goToProject = (projectId) => { onClose(); navigate(`/projects/${projectId}`); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="beneficiary-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Barlow'] text-xl" data-testid="beneficiary-detail-title">
            {t('expenses.beneficiaryDetail.title', { name: beneficiaryName })}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="p-10 text-center text-gray-400">{t('common.loading')}</div>
        ) : !data || data.expenses.length === 0 ? (
          <div className="p-10 text-center text-gray-400">{t('expenses.beneficiaryDetail.empty')}</div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
              <span className="font-bold text-gray-900" data-testid="beneficiary-detail-total">${data.total.toFixed(2)}</span>
              <span className="text-gray-500">{t('expenses.beneficiaryDetail.expenseCount', { count: data.count })}</span>
            </div>
            <p className="text-xs text-gray-400 italic" data-testid="beneficiary-detail-readonly-note">
              {t('expenses.beneficiaryDetail.readOnlyNote')}
            </p>
            <div className="overflow-x-auto border border-gray-100 rounded-lg">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-3 py-2">{t('expenses.beneficiaryDetail.date')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-3 py-2">{t('expenses.beneficiaryDetail.concept')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-3 py-2">{t('expenses.beneficiaryDetail.method')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-3 py-2">{t('expenses.beneficiaryDetail.status')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-3 py-2">{t('expenses.beneficiaryDetail.amount')}</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.expenses.map(e => (
                    <tr key={e.id} data-testid={`beneficiary-detail-row-${e.id}`} className="border-b border-gray-50 last:border-0">
                      <td className="px-3 py-2 text-sm text-gray-600">{(e.date || '').slice(0, 10) || '—'}</td>
                      <td className="px-3 py-2 text-sm text-gray-700 max-w-[200px] truncate">
                        {e.item_description || e.description || t(`expenses.types.${e.expense_type}`, { defaultValue: e.expense_type }) || '—'}
                        {e.project_number && (
                          <button type="button" onClick={() => goToProject(e.project_id)} className="block text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5" data-testid={`beneficiary-detail-project-${e.id}`}>
                            {e.project_number}<ExternalLink className="w-3 h-3" />
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">{methodLabel(e, t)}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-md font-semibold ${e.payment_status === 'paid' ? STATUS_STYLE.paid : e.payment_status === 'unpaid' ? STATUS_STYLE.unpaid : 'bg-gray-100 text-gray-500'}`}>
                          {e.payment_status === 'paid' ? t('expenses.paid') : e.payment_status === 'unpaid' ? t('expenses.unpaid') : t('expenses.notSet')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-sm font-semibold text-gray-900">${(e.amount || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">
                        {e.receipt_file_id || e.receipt_url ? (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${e.receipt_url || `/api/expenses/receipts/${e.receipt_file_id}`}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline" data-testid={`beneficiary-detail-receipt-${e.id}`}>
                            <Eye className="w-3.5 h-3.5" /> {t('expenses.beneficiaryDetail.viewReceipt')}
                          </a>
                        ) : <span className="text-gray-300"><FileText className="w-3.5 h-3.5" /></span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
